import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { extractProcedureIdentifier } from '@/utils/procedureIdentifierExtractor';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { formatDate } from '@/utils/dateFormatters';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Calendar, AlertCircle, FileText, Edit, Award, ArrowUpRight, RefreshCw, Activity, Info, LinkIcon, CheckCircle } from 'lucide-react';
import { isValidDateString } from '@/utils/dateValidation';
import { isContractAwardNotice } from '@/utils/procedureTypeUtils';
import { useTranslation } from 'react-i18next';
interface TenderTimelineProps {
  tender: any;
  publicationNumber: string;
}
interface RelatedNotice {
  publicationNumber: string;
  noticeType: string;
  publicationDate: string;
  title: string | null;
  buyerName: string | null;
  procedureIdentifier: string;
  isCurrentNotice?: boolean;
  isAwardNotice?: boolean;
}
export const TenderTimeline = ({
  tender,
  publicationNumber
}: TenderTimelineProps) => {
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const {
    t,
    i18n
  } = useTranslation('tenderView');
  const [procedureIdentifier, setProcedureIdentifier] = useState<string | null>(null);
  const [noticeIdentifier, setNoticeIdentifier] = useState<string | null>(null);
  useEffect(() => {
    if (tender) {
      const extractedProcId = extractProcedureIdentifier(tender);
      if (extractedProcId) {
        console.log('Found procedure identifier:', extractedProcId);
        setProcedureIdentifier(extractedProcId);
      } else {
        console.log('No procedure identifier found in tender data');
      }
      if (tender["notice-identifier"]) {
        setNoticeIdentifier(tender["notice-identifier"]);
      } else if (tender["BT-701-notice"]) {
        setNoticeIdentifier(tender["BT-701-notice"]);
      } else {
        if (tender.FT && Array.isArray(tender.FT) && tender.FT.length > 0) {
          for (const item of tender.FT) {
            if (typeof item === 'object') {
              const languages = Object.keys(item);
              for (const lang of languages) {
                const text = item[lang];
                if (typeof text === 'string') {
                  const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                  const match = text.match(uuidPattern);
                  if (match) {
                    if (match[0] !== extractedProcId) {
                      setNoticeIdentifier(match[0]);
                      break;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }, [tender]);
  const {
    data: timelineData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['tender-timeline', procedureIdentifier, publicationNumber],
    queryFn: async () => {
      if (!procedureIdentifier) {
        throw new Error('No procedure identifier available');
      }
      console.log(`Fetching related notices for procedure: ${procedureIdentifier}`);
      try {
        const {
          data,
          error
        } = await supabase.functions.invoke('search-related-notices', {
          body: {
            procedureIdentifier,
            publicationNumber
          }
        });
        if (error) {
          console.error('Error fetching related notices:', error);
          throw error;
        }
        if (!data) {
          console.error('No data returned from search-related-notices');
          throw new Error('No data returned from API');
        }
        console.log('Related notices data received:', data);
        if (data.notices && Array.isArray(data.notices)) {
          data.notices = data.notices.map((notice: any) => {
            const isAward = notice.noticeType?.toLowerCase().includes('award') || notice.noticeType === 'can-standard' || notice.tenderData && isContractAwardNotice(notice.tenderData);
            return {
              ...notice,
              isAwardNotice: isAward
            };
          });
        }
        return data;
      } catch (err) {
        console.error('Error in fetching related notices:', err);
        throw err;
      }
    },
    enabled: !!procedureIdentifier,
    retry: 1
  });
  const getNoticeTypeBadge = (noticeType: string, isAwardNotice?: boolean) => {
    if (isAwardNotice) {
      return <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
          <Award className="h-3 w-3" />
          <span>{t('timeline.award')}</span>
        </div>;
    }
    const type = noticeType.toLowerCase();
    if (type.includes('award') || type.includes('result') || type === 'can-standard') {
      return <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
          <Award className="h-3 w-3" />
          <span>{t('timeline.award')}</span>
        </div>;
    }
    if (type.includes('change') || type.includes('modification') || type.includes('corrigendum')) {
      return <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs">
          <Edit className="h-3 w-3" />
          <span>{t('timeline.change')}</span>
        </div>;
    }
    if (type.includes('notice') || type.includes('competition') || type === 'cn-standard') {
      return <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
          <FileText className="h-3 w-3" />
          <span>{t('timeline.notice')}</span>
        </div>;
    }
    if (type.includes('pin') || type === 'pin-only') {
      return <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
          <Info className="h-3 w-3" />
          <span>{t('timeline.priorInfo')}</span>
        </div>;
    }
    return <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
        <FileText className="h-3 w-3" />
        <span>{noticeType}</span>
      </div>;
  };
  const handleViewNotice = (publicationNumber: string) => {
    navigate(`/tender-view/${publicationNumber}`);
  };
  if (!procedureIdentifier) {
    return <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5 text-gray-500" />
            {t('timeline.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6 text-center text-gray-500">
            <AlertCircle className="h-8 w-8 mb-2 text-gray-400" />
            <p>{t('timeline.noProcedureIdentifier')}</p>
            <p className="text-sm mt-1">
              {t('timeline.noTimelineReason')}
            </p>
          </div>
        </CardContent>
      </Card>;
  }
  if (isLoading) {
    return <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5 text-gray-500" />
            {t('timeline.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            <p className="text-sm text-gray-500 mt-4">{t('timeline.loading')}</p>
          </div>
        </CardContent>
      </Card>;
  }
  if (!timelineData || !timelineData.notices || timelineData.notices.length === 0) {
    return <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5 text-gray-500" />
            {t('timeline.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <AlertCircle className="h-8 w-8 mb-2 text-amber-500" />
            <p className="text-gray-700">{t('timeline.noRelatedNotices')}</p>
            <p className="text-sm text-gray-500 mt-1">
              {t('timeline.procedureIdentifier')}: {procedureIdentifier}
            </p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" /> {t('timeline.retry')}
            </Button>
          </div>
        </CardContent>
      </Card>;
  }
  const {
    notices
  } = timelineData;
  return <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="h-5 w-5 text-gray-500" />
          {t('timeline.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-xs text-gray-500 mb-4 space-y-2">
          
          
          {noticeIdentifier && noticeIdentifier !== procedureIdentifier}
        </div>
        
        <div className="space-y-4">
          {notices.map((notice: RelatedNotice, index: number) => <div key={notice.publicationNumber} className={`relative ${notice.isCurrentNotice ? 'bg-blue-50 border border-blue-100 rounded-md p-3' : ''}`}>
              {index > 0 && <Separator className="absolute left-4 -top-4 h-4 w-0.5 bg-gray-200" />}
              
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <Calendar className="h-4 w-4 text-gray-400" />
                </div>
                
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        {getNoticeTypeBadge(notice.noticeType, notice.isAwardNotice)}
                        <span className="text-xs text-gray-500">
                          {isValidDateString(notice.publicationDate) ? formatDate(notice.publicationDate) : t('timeline.unknownDate')}
                        </span>
                        
                        {notice.isAwardNotice && <span className="flex items-center text-xs text-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {t('timeline.awarded')}
                          </span>}
                      </div>
                      
                      <h3 className="font-medium text-sm mt-1">
                        {notice.title || notice.noticeType}
                      </h3>
                      
                      {notice.buyerName && <p className="text-xs text-gray-600 mt-1">
                          {notice.buyerName}
                        </p>}
                    </div>
                    
                    {!notice.isCurrentNotice && <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => handleViewNotice(notice.publicationNumber)}>
                        {t('timeline.view')}
                        <ArrowUpRight className="h-3 w-3 ml-1" />
                      </Button>}
                  </div>
                  
                  <div className="mt-1 text-xs text-gray-500">
                    {t('timeline.publication')}: {notice.publicationNumber}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
        
        
      </CardContent>
    </Card>;
};